import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  CalendarToday as CalendarIcon,
  Email as EmailIcon,
  Language as WebsiteIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  Work as WorkIcon,
  School as SchoolIcon,
  Edit as EditIcon,
  Share as ShareIcon,
  LinkedIn as LinkedInIcon,
  Twitter as TwitterIcon,
  GitHub as GitHubIcon,
  CheckCircle as CheckIcon,
  HourglassEmpty as PendingIcon,
  CheckCircleOutline as ApprovedIcon,
  Cancel as RejectedIcon,
  Block as CancelledIcon,
  Description as DocumentIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  TableChart as ExcelIcon,
  Slideshow as PowerpointIcon,
  TextSnippet as TextIcon,
  Public as PublicIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { UserService } from '../services/userService';
import { ProfileImageService } from '../services/profileImageService';
import { InquiryService } from '../services/inquiryService';
import ProfileEditModal from '../components/Profile/ProfileEditModal';
import ProfileVisibilityPopup from '../components/Profile/ProfileVisibilityPopup';
import UnauthenticatedAccessDenied from '../components/Auth/UnauthenticatedAccessDenied';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

const gradientBg = {
  background: 'linear-gradient(135deg, #0ea5e9 0%, #38bdf8 50%, #93c5fd 100%)',
};

const JobProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { id: routeUserId } = useParams<{ id: string }>();
  const { user } = useAuthStore();

  const targetUserId = useMemo(() => routeUserId || user?.id || null, [routeUserId, user?.id]);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<any | null>(null);
  const [bannerUrl, setBannerUrl] = useState<string | undefined>(undefined);
  const [avatarUrl, setAvatarUrl] = useState<string | undefined>(undefined);
  const [editOpen, setEditOpen] = useState(false);
  const [visibilityPopupOpen, setVisibilityPopupOpen] = useState(false);
  const [canViewProfile, setCanViewProfile] = useState<boolean | null>(null);
  const [accessChecked, setAccessChecked] = useState(false);
  const [existingInquiry, setExistingInquiry] = useState<{
    status: 'pending' | 'approved' | 'rejected' | 'cancelled';
    date: string;
  } | null>(null);
  const [publicDocuments, setPublicDocuments] = useState<any[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);

  const isOwnProfile = useMemo(() => !!(user?.id && targetUserId && user.id === targetUserId), [user?.id, targetUserId]);

  useEffect(() => {
    const run = async () => {
      if (!targetUserId) {
        setLoading(false);
        setAccessChecked(true);
        return;
      }

      setLoading(true);
      try {
        // First check if user can access this profile
        let canView = false;
        if (!user?.id) {
          // Unauthenticated users - we'll check after loading profile data
          canView = false;
        } else if (user.id === targetUserId) {
          // Own profile is always accessible
          canView = true;
        } else {
          // Check if user can view other's profile
          canView = await InquiryService.canViewProfile(targetUserId, user.id);
        }

        // Load profile data
        const data = await UserService.getUserProfile(targetUserId);
        setProfile(data);
        
        // For unauthenticated users, check if profile is public
        if (!user?.id) {
          canView = data.is_public ?? false;
        }
        
        setCanViewProfile(canView);

        // Only load images if user has access
        if (canView) {
          try {
            const images = await ProfileImageService.getUserProfileImages(targetUserId);
            setBannerUrl(images?.banner_image_url || undefined);
            setAvatarUrl(images?.profile_picture_url || undefined);
          } catch (e) {
            // non-fatal
          }
        }

        // Check for existing inquiry if user can't view profile
        if (!canView && !isOwnProfile && user?.id) {
          try {
            // Check localStorage first
            const localStorageKey = `inquiry_${user.id}_${targetUserId}`;
            const storedInquiry = localStorage.getItem(localStorageKey);
            
            if (storedInquiry) {
              const inquiryData = JSON.parse(storedInquiry);
              setExistingInquiry({
                status: inquiryData.status,
                date: inquiryData.date
              });
            } else {
              // Check database for existing inquiry
              const hasPending = await InquiryService.hasPendingInquiry(targetUserId, user.id);
              if (hasPending) {
                const inquiries = await InquiryService.getInquiriesByUser(user.id);
                const inquiry = inquiries.find(i => 
                  i.profile_user_id === targetUserId && 
                  i.status === 'pending'
                );
                
                if (inquiry) {
                  const inquiryData = {
                    status: inquiry.status,
                    date: inquiry.created_at
                  };
                  setExistingInquiry(inquiryData);
                  localStorage.setItem(localStorageKey, JSON.stringify(inquiryData));
                }
              }
            }
          } catch (error) {
            console.error('Error checking existing inquiry:', error);
          }
        }

        // Mark access as checked
        setAccessChecked(true);
        
        // Always show popup - either for own profile or to show access status
        const timer = setTimeout(() => {
          setVisibilityPopupOpen(true);
        }, 1000);
        
        return () => clearTimeout(timer);
      } catch (error) {
        console.error('Error loading profile:', error);
        setCanViewProfile(false);
        setAccessChecked(true);
        // Still show popup to inform user
        const timer = setTimeout(() => {
          setVisibilityPopupOpen(true);
        }, 1000);
        
        return () => clearTimeout(timer);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [targetUserId, user?.id, isOwnProfile]);

  // Fetch public documents for the user
  useEffect(() => {
    const fetchPublicDocuments = async () => {
      if (!targetUserId) return;

      setDocumentsLoading(true);
      try {
        const { data: documents, error } = await supabase
          .from('documents')
          .select('*')
          .eq('user_id', targetUserId)
          .eq('is_public', true)
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Error fetching public documents:', error);
          setPublicDocuments([]);
        } else {
          setPublicDocuments(documents || []);
        }
      } catch (err) {
        console.error('Error fetching public documents:', err);
        setPublicDocuments([]);
      } finally {
        setDocumentsLoading(false);
      }
    };

    fetchPublicDocuments();
  }, [targetUserId]);


  const fullName = useMemo(() => {
    if (loading) return '';
    const first = profile?.first_name || '';
    const last = profile?.last_name || '';
    const combined = `${first} ${last}`.trim();
    return combined || 'Professional';
  }, [loading, profile]);

  // Normalize possibly string/JSON fields coming from DB
  const skills: string[] = useMemo(() => {
    let s: any = profile?.skills;
    if (typeof s === 'string') {
      try { s = JSON.parse(s); } catch { s = s ? [s] : []; }
    }
    if (!Array.isArray(s)) s = [];

    // Fall back to interests if skills are not present
    let interests: any = profile?.interests;
    if (typeof interests === 'string') {
      try { interests = JSON.parse(interests); } catch { interests = interests ? [interests] : []; }
    }
    if (!Array.isArray(interests)) interests = [];

    const merged = [...s, ...interests];
    return merged.map((v) => String(v)).filter(Boolean);
  }, [profile]);

  const workExperience: any[] = useMemo(() => {
    let w: any = profile?.work_experience;
    if (typeof w === 'string') {
      try { w = JSON.parse(w); } catch { w = w ? [w] : []; }
    }
    if (Array.isArray(w)) return w;
    if (w && typeof w === 'object') return [w];
    return [];
  }, [profile]);

  const education: any[] = useMemo(() => {
    let e: any = profile?.education;
    if (typeof e === 'string') {
      try { e = JSON.parse(e); } catch { e = e ? [e] : []; }
    }
    if (Array.isArray(e)) return e;
    if (e && typeof e === 'object') return [e];
    return [];
  }, [profile]);

  const website: string | undefined = useMemo(() => {
    if (profile?.website) return profile.website;
    let links: any = profile?.social_media_links;
    if (typeof links === 'string') {
      try { links = JSON.parse(links); } catch { links = undefined; }
    }
    return links?.website;
  }, [profile]);

  const socialLinks: Record<string, string> = useMemo(() => {
    let links: any = profile?.social_media_links;
    if (typeof links === 'string') {
      try { links = JSON.parse(links); } catch { links = {}; }
    }
    const out: Record<string, string> = {};
    if (website) out.website = website;
    if (links && typeof links === 'object') {
      for (const k of Object.keys(links)) {
        const v = String((links as any)[k] || '').trim();
        if (v) out[k] = v;
      }
    }
    return out;
  }, [profile, website]);

  const getSocialIcon = (key: string) => {
    const k = key.toLowerCase();
    if (k.includes('linkedin')) return <LinkedInIcon sx={{ mr: 1 }} />;
    if (k.includes('twitter') || k === 'x') return <TwitterIcon sx={{ mr: 1 }} />;
    if (k.includes('github')) return <GitHubIcon sx={{ mr: 1 }} />;
    if (k.includes('website') || k.includes('site')) return <WebsiteIcon sx={{ mr: 1 }} />;
    return <WebsiteIcon sx={{ mr: 1 }} />;
  };

  // Document helper functions
  const getFileIcon = (fileType: string) => {
    if (fileType.includes('pdf')) return <PdfIcon sx={{ color: '#d32f2f', fontSize: 24 }} />;
    if (fileType.includes('image')) return <ImageIcon sx={{ color: '#1976d2', fontSize: 24 }} />;
    if (fileType.includes('excel') || fileType.includes('spreadsheet')) return <ExcelIcon sx={{ color: '#2e7d32', fontSize: 24 }} />;
    if (fileType.includes('powerpoint') || fileType.includes('presentation')) return <PowerpointIcon sx={{ color: '#f57c00', fontSize: 24 }} />;
    if (fileType.includes('text')) return <TextIcon sx={{ color: '#424242', fontSize: 24 }} />;
    return <DocumentIcon sx={{ color: '#666', fontSize: 24 }} />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getDocumentTypeLabel = (documentType: string) => {
    const typeMap: { [key: string]: string } = {
      'cv': 'CV/Resume',
      'cover_letter': 'Cover Letter',
      'certificate': 'Certificate',
      'diploma': 'Diploma',
      'portfolio': 'Portfolio',
      'other': 'Other',
      'employment_document': 'Employment Document',
      'license': 'License',
      'contract': 'Contract'
    };
    return typeMap[documentType] || documentType;
  };

  const handleDownloadDocument = async (doc: any) => {
    try {
      const { data, error } = await supabase.storage
        .from('user-documents')
        .download(doc.file_path);

      if (error) {
        console.error('Error downloading document:', error);
        toast.error('Failed to download document');
        return;
      }

      const url = URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.file_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success('Document downloaded successfully!');
    } catch (err) {
      console.error('Error downloading document:', err);
      toast.error('Failed to download document');
    }
  };

  const handleViewDocument = async (doc: any) => {
    try {
      const { data, error } = await supabase.storage
        .from('user-documents')
        .createSignedUrl(doc.file_path, 3600); // 1 hour expiry

      if (error) {
        console.error('Error creating signed URL:', error);
        toast.error('Failed to open document');
        return;
      }

      window.open(data.signedUrl, '_blank');
    } catch (err) {
      console.error('Error viewing document:', err);
      toast.error('Failed to open document');
    }
  };

  const handleSaveProfile = (updatedData: any) => {
    setProfile((prev: any) => ({
      ...prev,
      first_name: updatedData.firstName ?? prev?.first_name,
      last_name: updatedData.lastName ?? prev?.last_name,
      email: updatedData.email ?? prev?.email,
      location: updatedData.location ?? prev?.location,
      bio: updatedData.bio ?? prev?.bio,
      work_experience: updatedData.workExperience ?? prev?.work_experience,
      education: updatedData.education ?? prev?.education,
      social_media_links: updatedData.socialMediaLinks ?? prev?.social_media_links,
    }));
    if (updatedData.avatar) setAvatarUrl(updatedData.avatar);
    if (updatedData.coverImage) setBannerUrl(updatedData.coverImage);
    setEditOpen(false);
  };

  const handleImageDeleted = (type: 'avatar' | 'banner') => {
    if (type === 'avatar') setAvatarUrl(undefined);
    if (type === 'banner') setBannerUrl(undefined);
  };

  const handleShare = async () => {
    const shareUrl = window.location.href;
    const title = fullName || 'Profile';
    try {
      if (navigator.share) {
        await navigator.share({ title, url: shareUrl });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        // Optional: simple toast without bringing in a new dependency
        alert('Profile link copied to clipboard');
      }
    } catch (e) {
      // ignore
    }
  };

  const handleToggleVisibility = async () => {
    if (!user?.id) return;
    
    try {
      const newVisibility = !profile?.is_public;
      const result = await UserService.updateUserProfileDirect(user.id, {
        is_public: newVisibility
      });
      
      if (result.success) {
        setProfile((prev: any) => ({
          ...prev,
          is_public: newVisibility
        }));
        setVisibilityPopupOpen(false);
        // Show success message
        alert(`Profile is now ${newVisibility ? 'public' : 'private'}`);
      } else {
        alert('Failed to update profile visibility. Please try again.');
      }
    } catch (error) {
      console.error('Error toggling profile visibility:', error);
      alert('Failed to update profile visibility. Please try again.');
    }
  };

  // Show loading state while checking access
  if (!accessChecked || loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Checking profile access...
          </Typography>
        </Box>
      </Box>
    );
  }

  // If user is not authenticated and profile is private, show access denied
  if (!user?.id && !canViewProfile) {
    return <UnauthenticatedAccessDenied />;
  }

  // If user can't view profile and it's not their own, show limited content
  if (!isOwnProfile && canViewProfile === false) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50' }}>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Card sx={{ borderRadius: 3, boxShadow: 2, textAlign: 'center', py: 6 }}>
            <CardContent>
              <Typography variant="h4" fontWeight="bold" gutterBottom color="text.secondary">
                Profile Not Accessible
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                This profile is private and you don't have permission to view it.
              </Typography>

              {/* Existing inquiry status */}
              {existingInquiry && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                    p: 3,
                    mb: 3,
                    borderRadius: 3,
                    background: existingInquiry.status === 'approved' 
                      ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(16, 185, 129, 0.1) 100%)'
                      : existingInquiry.status === 'rejected'
                      ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%)'
                      : existingInquiry.status === 'cancelled'
                      ? 'linear-gradient(135deg, rgba(107, 114, 128, 0.1) 0%, rgba(75, 85, 99, 0.1) 100%)'
                      : 'linear-gradient(135deg, rgba(251, 191, 36, 0.1) 0%, rgba(245, 158, 11, 0.1) 100%)',
                    border: existingInquiry.status === 'approved'
                      ? '1px solid rgba(34, 197, 94, 0.2)'
                      : existingInquiry.status === 'rejected'
                      ? '1px solid rgba(239, 68, 68, 0.2)'
                      : existingInquiry.status === 'cancelled'
                      ? '1px solid rgba(107, 114, 128, 0.2)'
                      : '1px solid rgba(251, 191, 36, 0.2)',
                  }}
                >
                  {existingInquiry.status === 'approved' && (
                    <ApprovedIcon sx={{ fontSize: 32, color: '#22c55e' }} />
                  )}
                  {existingInquiry.status === 'rejected' && (
                    <RejectedIcon sx={{ fontSize: 32, color: '#ef4444' }} />
                  )}
                  {existingInquiry.status === 'cancelled' && (
                    <CancelledIcon sx={{ fontSize: 32, color: '#6b7280' }} />
                  )}
                  {existingInquiry.status === 'pending' && (
                    <PendingIcon sx={{ fontSize: 32, color: '#fbbf24' }} />
                  )}
                  
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography
                      variant="h6"
                      fontWeight={700}
                      sx={{
                        color: existingInquiry.status === 'approved'
                          ? '#22c55e'
                          : existingInquiry.status === 'rejected'
                          ? '#ef4444'
                          : existingInquiry.status === 'cancelled'
                          ? '#6b7280'
                          : '#fbbf24',
                        mb: 0.5,
                      }}
                    >
                      {existingInquiry.status === 'approved' && 'Inquiry Approved!'}
                      {existingInquiry.status === 'rejected' && 'Inquiry Rejected'}
                      {existingInquiry.status === 'cancelled' && 'Inquiry Cancelled'}
                      {existingInquiry.status === 'pending' && 'Inquiry Pending'}
                    </Typography>
                    
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 500,
                      }}
                    >
                      {existingInquiry.status === 'approved' && 'You can now view this profile!'}
                      {existingInquiry.status === 'rejected' && 'The profile owner declined your request.'}
                      {existingInquiry.status === 'cancelled' && 'This inquiry was cancelled.'}
                      {existingInquiry.status === 'pending' && `Sent on ${new Date(existingInquiry.date).toLocaleDateString()}`}
                    </Typography>
                  </Box>
                </Box>
              )}
              
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                {!existingInquiry && (
                  <Button
                    variant="contained"
                    onClick={() => setVisibilityPopupOpen(true)}
                    sx={{
                      textTransform: 'none',
                      background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                      color: 'white',
                      fontWeight: 700,
                      px: 4,
                      py: 1.5,
                      borderRadius: 3,
                      boxShadow: '0 8px 25px rgba(59, 130, 246, 0.3)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
                        boxShadow: '0 12px 35px rgba(59, 130, 246, 0.4)',
                        transform: 'translateY(-2px)',
                      },
                      transition: 'all 0.3s ease-in-out',
                      fontSize: '1rem',
                    }}
                  >
                    Send Inquiry Request
                  </Button>
                )}
                
                <Button
                  variant="outlined"
                  onClick={() => navigate(-1)}
                  sx={{
                    textTransform: 'none',
                    borderColor: 'grey.300',
                    color: 'text.secondary',
                    fontWeight: 600,
                    px: 4,
                    py: 1.5,
                    borderRadius: 3,
                    '&:hover': {
                      borderColor: 'grey.400',
                      backgroundColor: 'grey.50',
                      transform: 'translateY(-1px)',
                    },
                    transition: 'all 0.2s ease-in-out',
                    fontSize: '1rem',
                  }}
                >
                  Go Back
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Container>

        {/* Profile Visibility Popup */}
        <ProfileVisibilityPopup
          open={visibilityPopupOpen}
          onClose={() => setVisibilityPopupOpen(false)}
          isPublic={profile?.is_public ?? false}
          onToggleVisibility={handleToggleVisibility}
          isOwnProfile={isOwnProfile}
          profileUserId={targetUserId}
          currentUserId={user?.id}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50' }}>
      {/* Banner image (like normal profile) */}
      <Box sx={{ position: 'relative' }}>
        <Box
          sx={{
            height: { xs: 200, md: 250 },
            backgroundImage: bannerUrl ? `url(${bannerUrl})` : gradientBg.background,
            backgroundSize: bannerUrl ? 'cover' : 'auto',
            backgroundPosition: 'center',
          }}
        />

        {/* Top actions and breadcrumbs */}
        <Container maxWidth="lg" sx={{ position: 'absolute', top: 8, left: 0, right: 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <IconButton onClick={() => navigate(-1)} sx={{ color: 'white' }}>
              <BackIcon />
            </IconButton>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton onClick={handleShare} sx={{ color: 'white', bgcolor: 'rgba(0,0,0,0.25)' }}>
                <ShareIcon />
              </IconButton>
              {isOwnProfile && (
                <Button
                  onClick={() => setEditOpen(true)}
                  startIcon={<EditIcon />}
                  variant="contained"
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.9)',
                    color: 'primary.dark',
                    fontWeight: 700,
                    textTransform: 'none',
                    boxShadow: '0 10px 25px rgba(14,165,233,0.35)',
                    '&:hover': { bgcolor: 'white' },
                  }}
                >
                  Edit Profile
                </Button>
              )}
            </Box>
          </Box>
        </Container>

        {/* Avatar and basic info overlay */}
        <Container maxWidth="lg">
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: { xs: 'center', md: 'flex-end' },
              gap: 3,
              flexWrap: 'wrap',
              transform: 'translateY(-48px)'
            }}
          >
            <Avatar
              src={avatarUrl}
              sx={{ width: 100, height: 100, border: '4px solid white', boxShadow: 3, bgcolor: 'grey.200' }}
            >
              <PersonIcon />
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 260 }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  px: { xs: 2, md: 3 },
                  py: { xs: 1.25, md: 1.5 },
                  borderRadius: 3,
                  bgcolor: 'rgba(255,255,255,0.9)',
                  backdropFilter: 'saturate(140%) blur(8px)',
                  boxShadow: '0 12px 36px rgba(2, 132, 199, 0.30)',
                  border: '1px solid rgba(255,255,255,0.9)',
                }}
              >
                <Typography variant="h4" fontWeight={800} color="text.primary">
                  {loading ? <Skeleton width={260} /> : fullName}
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: 'primary.dark',
                    fontWeight: 600,
                    px: 1,
                    borderRadius: 1,
                    display: 'inline-block',
                    background: 'linear-gradient(135deg, rgba(14,165,233,0.18), rgba(147,197,253,0.18))',
                  }}
                >
                  {loading ? <Skeleton width={180} /> : (profile?.headline || 'Open to opportunities')}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 1, mt: 1.25, flexWrap: 'wrap', alignItems: 'center' }}>
                {skills.length > 0 && (
                  <Typography variant="caption" sx={{ mr: 0.5, fontWeight: 700, color: 'text.secondary' }}>
                    Interests:
                  </Typography>
                )}
                {skills.slice(0, 8).map((s: string, i: number) => (
                  <Chip
                    key={i}
                    label={s}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(14,165,233,0.1)',
                      border: '1px solid rgba(14,165,233,0.25)',
                      color: 'primary.dark',
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' }, gap: 3 }}>
          <Box>
            <Card sx={{ borderRadius: 3, boxShadow: 2, overflow: 'hidden' }}>
              <CardContent>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Contact
                </Typography>
                {loading ? (
                  <Skeleton variant="rectangular" height={120} />
                ) : (
                  <List>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon>
                        <EmailIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={profile?.email || user?.email || '—'} />
                    </ListItem>
                    {profile?.phone && (
                      <ListItem sx={{ px: 0 }}>
                        <ListItemIcon>
                          <PhoneIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText primary={profile?.phone} />
                      </ListItem>
                    )}
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon>
                        <LocationIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={profile?.location || '—'} />
                    </ListItem>
                    {website && (
                      <ListItem sx={{ px: 0 }}>
                        <ListItemIcon>
                          <WebsiteIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText primary={website} />
                      </ListItem>
                    )}
                    {profile?.created_at && (
                      <ListItem sx={{ px: 0 }}>
                        <ListItemIcon>
                          <CalendarIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText primary={`Member since ${new Date(profile.created_at).getFullYear()}`} />
                      </ListItem>
                    )}
                  </List>
                )}
                <Divider sx={{ my: 2 }} />
                {isOwnProfile ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Chip
                      icon={<CheckIcon />}
                      label="This is your profile"
                      color="success"
                      variant="outlined"
                      sx={{ fontWeight: 700, px: 1.5, py: 1 }}
                    />
                  </Box>
                ) : (
                  <Button fullWidth variant="contained" size="large">
                    Contact Candidate
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Documents Tile */}
            <Card sx={{ mt: 3, borderRadius: 3, boxShadow: 2, overflow: 'hidden' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <DocumentIcon color="primary" />
                  <Typography variant="h6" fontWeight={700}>
                    Documents
                  </Typography>
                  <PublicIcon sx={{ color: 'success.main', fontSize: 20 }} />
                </Box>
                
                {documentsLoading ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {[1, 2, 3].map((index) => (
                      <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 2 }}>
                        <Skeleton variant="circular" width={40} height={40} />
                        <Box sx={{ flex: 1 }}>
                          <Skeleton variant="text" width="60%" height={24} />
                          <Skeleton variant="text" width="40%" height={20} />
                        </Box>
                        <Skeleton variant="rectangular" width={80} height={32} />
                      </Box>
                    ))}
                  </Box>
                ) : publicDocuments.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <DocumentIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No public documents
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {isOwnProfile 
                        ? "You haven't made any documents public yet. Make documents public in your document manager to share them here."
                        : `${profile?.first_name || 'This user'} hasn't shared any public documents yet.`
                      }
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {publicDocuments.map((document, index) => (
                      <Box
                        key={document.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          p: 2,
                          border: 1,
                          borderColor: 'divider',
                          borderRadius: 2,
                          backgroundColor: 'background.paper',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            borderColor: 'primary.main',
                            boxShadow: 1,
                            transform: 'translateY(-1px)',
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 48, height: 48 }}>
                          {getFileIcon(document.file_type)}
                        </Box>
                        
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="subtitle1" fontWeight="bold" noWrap>
                            {document.file_name}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            <Chip
                              label={getDocumentTypeLabel(document.document_type)}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ fontSize: '0.75rem', height: 20 }}
                            />
                            <Typography variant="caption" color="text.secondary">
                              {formatFileSize(document.file_size)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              •
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(document.created_at).toLocaleDateString()}
                            </Typography>
                          </Box>
                          {document.description && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }} noWrap>
                              {document.description}
                            </Typography>
                          )}
                        </Box>
                        
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<ViewIcon />}
                            onClick={() => handleViewDocument(document)}
                            sx={{
                              minWidth: 'auto',
                              px: 1.5,
                              py: 0.5,
                              fontSize: '0.75rem',
                              textTransform: 'none',
                            }}
                          >
                            View
                          </Button>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<DownloadIcon />}
                            onClick={() => handleDownloadDocument(document)}
                            sx={{
                              minWidth: 'auto',
                              px: 1.5,
                              py: 0.5,
                              fontSize: '0.75rem',
                              textTransform: 'none',
                              bgcolor: 'success.main',
                              '&:hover': {
                                bgcolor: 'success.dark',
                              },
                            }}
                          >
                            Download
                          </Button>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>

          <Box>
            <Card sx={{ mb: 3, borderRadius: 3, boxShadow: 2, position: 'relative', overflow: 'hidden' }}>
              <CardContent>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon color="primary" /> About
                  </Box>
                </Typography>
                {loading ? (
                  <Skeleton height={80} />
                ) : (
                  <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                    {(profile?.bio || profile?.description || '').trim() || 'No bio provided.'}
                  </Typography>
                )}
              </CardContent>
            </Card>

            {/* Social Media */}
            {Object.keys(socialLinks).length > 0 && (
              <Card sx={{ mb: 3, borderRadius: 3, boxShadow: 2 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={700} gutterBottom>
                    Social Media
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {Object.entries(socialLinks).map(([k, v]) => (
                      <Button
                        key={k}
                        variant="outlined"
                        size="small"
                        href={v.startsWith('http') ? v : `https://${v}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ textTransform: 'none', borderRadius: 2, display: 'inline-flex', alignItems: 'center', gap: 1 }}
                      >
                        {getSocialIcon(k)}
                        {k.charAt(0).toUpperCase() + k.slice(1)}
                      </Button>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            )}

            <Card sx={{ mb: 3, borderRadius: 3, boxShadow: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <WorkIcon color="primary" />
                  <Typography variant="h6" fontWeight={700}>Work Experience</Typography>
                </Box>
                {loading ? (
                  <Skeleton height={120} />
                ) : (
                  <List sx={{ mt: 1, pl: 0 }}>
                    {workExperience.map((exp: any, i: number) => (
                      <ListItem key={i} alignItems="flex-start" sx={{ px: 0, position: 'relative' }}>
                        <Box sx={{ width: 8, height: 8, bgcolor: 'primary.main', borderRadius: '50%', mt: 1.1, mr: 1 }} />
                        {typeof exp === 'string' ? (
                          <ListItemText
                            primary={<Typography variant="body1">{exp}</Typography>}
                          />
                        ) : (
                          <ListItemText
                            primary={<Typography fontWeight={700}>{exp.title || exp.role || 'Role'}</Typography>}
                            secondary={
                              <>
                                <Typography variant="body2" color="text.secondary">{exp.company || 'Company'} • {exp.location || ''}</Typography>
                                <Typography variant="body2" color="text.secondary">{exp.startDate || exp.start_date || ''} – {exp.endDate || exp.end_date || 'Present'}</Typography>
                                {exp.description && (
                                  <Typography variant="body2" sx={{ mt: 0.5 }}>{exp.description}</Typography>
                                )}
                              </>
                            }
                          />
                        )}
                      </ListItem>
                    ))}
                    {workExperience.length === 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>No experience added yet.</Typography>
                    )}
                  </List>
                )}
              </CardContent>
            </Card>

            <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <SchoolIcon color="primary" />
                  <Typography variant="h6" fontWeight={700}>Education</Typography>
                </Box>
                {loading ? (
                  <Skeleton height={100} />
                ) : (
                  <List sx={{ mt: 1, pl: 0 }}>
                    {education.map((ed: any, i: number) => (
                      <ListItem key={i} alignItems="flex-start" sx={{ px: 0 }}>
                        <Box sx={{ width: 8, height: 8, bgcolor: 'primary.light', borderRadius: '50%', mt: 1.1, mr: 1 }} />
                        {typeof ed === 'string' ? (
                          <ListItemText primary={<Typography variant="body1">{ed}</Typography>} />
                        ) : (
                          <ListItemText
                            primary={<Typography fontWeight={700}>{ed.degree || ed.title || 'Education'}</Typography>}
                            secondary={
                              <>
                                <Typography variant="body2" color="text.secondary">{ed.school || ed.institution || 'Institution'}</Typography>
                                <Typography variant="body2" color="text.secondary">{ed.startDate || ed.start_date || ''} – {ed.endDate || ed.end_date || ''}</Typography>
                              </>
                            }
                          />
                        )}
                      </ListItem>
                    ))}
                    {education.length === 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>No education added yet.</Typography>
                    )}
                  </List>
                )}
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Container>

      {/* Edit Profile Modal */}
      {isOwnProfile && (
        <ProfileEditModal
          open={editOpen}
          onClose={() => setEditOpen(false)}
          onSave={handleSaveProfile}
          onImageDeleted={handleImageDeleted}
          initialData={{
            firstName: profile?.first_name || '',
            lastName: profile?.last_name || '',
            email: profile?.email || user?.email || '',
            location: profile?.location || '',
            bio: profile?.bio || '',
            website: website || '',
            avatar: avatarUrl || '',
            coverImage: bannerUrl || '',
            interests: skills,
            isPublic: profile?.is_public ?? true,
            notifications: profile?.notifications_enabled ?? true,
            socialMediaLinks: typeof profile?.social_media_links === 'object' ? profile?.social_media_links : {},
            selectedAddressId: undefined,
            workExperience: '',
            education: '',
          }}
        />
      )}

      {/* Profile Visibility Popup */}
      <ProfileVisibilityPopup
        open={visibilityPopupOpen}
        onClose={() => setVisibilityPopupOpen(false)}
        isPublic={profile?.is_public ?? true}
        onToggleVisibility={handleToggleVisibility}
        isOwnProfile={isOwnProfile}
        profileUserId={targetUserId}
        currentUserId={user?.id}
      />
    </Box>
  );
};

export default JobProfilePage;




